/**
 * The meetings log (BR-26).
 *
 * §1.1 names disputes from unrecorded agreements as the problem the whole
 * system addresses, so this screen has one job: make writing a decision down
 * easier than not writing it down.
 *
 * Attendance is compulsory and cannot be edited afterwards, which the form
 * says out loud rather than discovering at submit. A record of who agreed to
 * something is only worth having if it cannot be quietly changed later.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { usePartners } from '../finance/api.js';
import { useCreateMeeting, useMeetings } from './api.js';

const todayInDhaka = (): string => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dhaka' }).format(new Date());

export function MeetingsLog(): JSX.Element {
  const { t } = useTranslation();
  const { data: meetings, isLoading, isError, refetch } = useMeetings();
  const { data: partners } = usePartners();
  const createMeeting = useCreateMeeting();

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(todayInDhaka());
  const [attendees, setAttendees] = useState<number[]>([]);
  const [decisions, setDecisions] = useState('');
  const [error, setError] = useState<string | null>(null);

  const toggleAttendee = (id: number): void => {
    setAttendees((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  };

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    setError(null);

    createMeeting.mutate(
      { date, attendees, decisions: decisions.trim() },
      {
        onSuccess: () => {
          setDecisions('');
          setAttendees([]);
          setOpen(false);
        },
        onError: (err) => setError(t(errorMessageKey(err))),
      },
    );
  };

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('meetings.title')}</h1>

      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mb-4 min-h-touch w-full rounded-lg bg-green-700 px-4 py-3 text-sm font-medium text-white"
        >
          {t('meetings.record')}
        </button>
      )}

      {open && (
        <form onSubmit={submit} className="mb-4 rounded-xl bg-white p-4 shadow-sm">
          <label className="block text-xs text-gray-500" htmlFor="meeting-date">
            {t('meetings.date')}
          </label>
          <input
            id="meeting-date"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            required
            className="mt-1 min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />

          <fieldset className="mt-3">
            <legend className="text-xs text-gray-500">{t('meetings.attendees')}</legend>
            <p className="mb-1 text-xs text-gray-400">{t('meetings.attendeesFixed')}</p>

            <div className="space-y-1">
              {(partners ?? []).map((partner) => (
                <label key={partner.partnerId} className="flex min-h-touch items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={attendees.includes(partner.partnerId)}
                    onChange={() => toggleAttendee(partner.partnerId)}
                  />
                  {partner.fullName}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="mt-3 block text-xs text-gray-500" htmlFor="meeting-decisions">
            {t('meetings.decisions')}
          </label>
          <textarea
            id="meeting-decisions"
            value={decisions}
            onChange={(event) => setDecisions(event.target.value)}
            required
            rows={4}
            maxLength={5000}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />

          {error !== null && (
            <p role="alert" className="mt-2 text-xs text-red-700">
              {error}
            </p>
          )}

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="min-h-touch flex-1 rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-700"
            >
              {t('app.cancel')}
            </button>
            <button
              type="submit"
              disabled={createMeeting.isPending || attendees.length === 0 || decisions.trim().length < 3}
              className="min-h-touch flex-1 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white disabled:bg-gray-300"
            >
              {t('app.save')}
            </button>
          </div>
        </form>
      )}

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {meetings !== undefined && meetings.length === 0 && (
        <p className="py-8 text-center text-sm text-gray-500">{t('meetings.empty')}</p>
      )}

      <ul className="space-y-2">
        {(meetings ?? []).map((meeting) => (
          <li key={meeting.id} className="rounded-xl bg-white px-4 py-3 shadow-sm">
            <p className="text-xs text-gray-500">{meeting.date}</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{meeting.decisions}</p>
            <p className="mt-2 text-xs text-gray-500">
              {t('meetings.present', { names: meeting.attendees.map((a) => a.fullName).join(', ') })}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
